/**
 * Latency model. Everything is expressed in AudioContext ("graph") time.
 *
 * - A block the graph processes at graph time t is labelled t (the worklet's frame time).
 * - App-monitored sources (demo, files, captured tabs we replay): the analysed signal passes
 *   through a compensation DelayNode (D) and the output stack (outputLatency), so the sample
 *   labelled t is *heard* when the graph clock reads t + D + outputLatency.
 * - Externally monitored sources (mic, system audio): the listener hears the room directly, and a
 *   sound captured at wall time W is processed (and labelled) inputLatency later.
 *
 * For a frame that reaches the screen when the graph clock reads G, the matching analysis time is:
 *   app:       t = G − outputLatency − D
 *   external:  t = G + inputLatency       (usually in the future, so use the latest frame + beat prediction)
 */

export type Monitoring = 'app' | 'external';

export interface LatencyInfo {
  monitoring: Monitoring;
  /** AudioContext.outputLatency + baseLatency, seconds. */
  outputLatencyS: number;
  /** Capture latency for external sources, seconds. */
  inputLatencyS: number;
  /** Delay applied to the sound we play, seconds. */
  compensationDelayS: number;
}

export const DEFAULT_LATENCY: LatencyInfo = {
  monitoring: 'app',
  outputLatencyS: 0.02,
  inputLatencyS: 0.01,
  compensationDelayS: 0,
};

/** Analysis time that corresponds to what is heard when the graph clock reads `graphTime`. */
export function analysisTimeFor(graphTime: number, lat: LatencyInfo, syncOffsetS: number): number {
  const base =
    lat.monitoring === 'app'
      ? graphTime - lat.outputLatencyS - lat.compensationDelayS
      : graphTime + lat.inputLatencyS;
  return base - syncOffsetS;
}

/**
 * Delay to apply to app-monitored audio so the picture can keep up: the visual pipeline latency
 * plus a couple of analysis hops of margin, minus the latency the output stack already adds.
 */
export function compensationDelayFor(
  visualLatencyS: number,
  outputLatencyS: number,
  hopS: number,
): number {
  return Math.max(0, visualLatencyS + 2 * hopS - outputLatencyS);
}

/** Wall-clock milliseconds that are comparable across window, worker and worklet threads. */
export function absNowMs(): number {
  return performance.timeOrigin + performance.now();
}
